import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Checkbox } from '@appkit4/react-components/checkbox';
import { Modal as AppkitModal } from '@appkit4/react-components/modal';
import { Radio, RadioGroup } from '@appkit4/react-components/radio';
import { FileModel, Upload } from '@appkit4/react-components/upload';
import { CleanseDataType } from '../../api/dataCleanse';
import type { ImportUploadResponse } from '../../api/generated/models';
import CleanseFileSource from './CleanseFileSource';
import {
    PreviousImportColumn,
    preselectFromPreviousImport,
} from './importColumnPreselection';
import { DataSourceTypeId, ImportMode } from '../../Constants';
import useToast from '../../helpers/useToast';
import './ImportDataModal.scss';

export interface ImportColumnMapping {
    fieldName: string;
    displayName: string;
    sourceHeader: string;
}

export interface ImportWorksheetPreview {
    index?: number;
    name?: string | null;
    headers?: string[] | null;
    rowCount?: number;
    error?: string | null;
}

export interface ImportDataDraft {
    uploadId: number;
    worksheetIndex: number;
    worksheetName: string;
    baseMappings: ImportColumnMapping[];
    additionalHeaders: string[];
    mode: ImportMode;
    isFinalImport: boolean;
}

interface ImportDataModalProps {
    visible: boolean;
    onClose: () => void;
    dataSourceTypeId: DataSourceTypeId;
    uploadId?: number;
    worksheetPreviews?: ImportWorksheetPreview[];
    isPreviewLoading?: boolean;
    isSubmitting?: boolean;
    previewError?: string;
    /** Existing rows make replace meaningful, so the choice is only offered once there is data to lose. */
    hasExistingData?: boolean;
    /** Entity and period are needed to list that period's cleanse jobs. */
    entityId?: number;
    timeId?: number;
    /** Columns saved by the previous import; matched against the uploaded worksheet to pre-select the mapping. */
    previousColumns?: PreviousImportColumn[];
    onFileSelected?: (file: File) => void;
    onCleansedFileSelected?: (response: ImportUploadResponse) => void;
    onSubmit: (draft: ImportDataDraft) => void;
}

interface RequiredField {
    fieldName: string;
    displayName: string;
}

const fixedAssetFields: RequiredField[] = [
    { fieldName: 'AssetID', displayName: 'Asset ID' },
    { fieldName: 'AssetDescription', displayName: 'Asset Description' },
    { fieldName: 'TransactionDate', displayName: 'Transaction Date' },
    { fieldName: 'Cost', displayName: 'Cost' },
    { fieldName: 'Supplier', displayName: 'Supplier' },
];

const projectFields: RequiredField[] = [
    { fieldName: 'PackageDescription', displayName: 'Package Description' },
    { fieldName: 'Description', displayName: 'Description' },
    { fieldName: 'Total', displayName: 'Total' },
];

const maxFileSizeBytes = 100 * 1024 * 1024;
const maxProjectAdditionalColumns = 5;
const permittedExcelContentTypes = [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ImportDataModal: React.FC<ImportDataModalProps> = ({
    visible,
    onClose,
    dataSourceTypeId,
    uploadId,
    worksheetPreviews = [],
    isPreviewLoading = false,
    isSubmitting = false,
    previewError,
    hasExistingData = false,
    entityId,
    timeId,
    previousColumns,
    onFileSelected,
    onCleansedFileSelected,
    onSubmit,
}) => {
    const requiredFields =
        dataSourceTypeId === DataSourceTypeId.FixedAsset
            ? fixedAssetFields
            : projectFields;
    const isProjectData = dataSourceTypeId === DataSourceTypeId.Project;
    const [file, setFile] = useState<File>();
    const [source, setSource] = useState<'upload' | 'cleansed'>('upload');
    const [cleansedFileName, setCleansedFileName] = useState<string>();
    const [worksheetIndex, setWorksheetIndex] = useState(0);
    const [mappings, setMappings] = useState<Record<string, string>>({});
    const [additionalHeaders, setAdditionalHeaders] = useState<string[]>([]);
    const [mode, setMode] = useState<ImportMode>(ImportMode.Append);
    const [isFinalImport, setIsFinalImport] = useState(false);
    const [error, setError] = useState<string>();
    const [sections, setSections] = useState({
        upload: true,
        map: false,
        unmapped: false,
    });
    // Remembers which upload/worksheet was pre-selected, so a user's edits are never overwritten.
    const preselectedFor = useRef<string>();
    const showToast = useToast();

    const previews = worksheetPreviews.map((worksheet, index) => ({
        index: worksheet.index ?? index,
        name: worksheet.name ?? '',
        headers: worksheet.headers ?? [],
        rowCount: worksheet.rowCount ?? 0,
        error: worksheet.error,
    }));
    const selectedWorksheet = previews.find(
        (worksheet) => worksheet.index === worksheetIndex
    );
    const headers = selectedWorksheet?.error
        ? []
        : (selectedWorksheet?.headers ?? []);
    const mappedHeaders = Object.values(mappings).filter(Boolean);
    const unmappedHeaders = headers.filter(
        (header) => !mappedHeaders.includes(header)
    );
    const isMappingComplete = requiredFields.every((field) =>
        Boolean(mappings[field.fieldName])
    );
    const hasTooManyAdditionalColumns =
        isProjectData && additionalHeaders.length > maxProjectAdditionalColumns;
    const mappingHeaders = useMemo(
        () =>
            dataSourceTypeId === DataSourceTypeId.Project
                ? headers.slice(0, 40)
                : headers,
        [dataSourceTypeId, headers]
    );

    useEffect(() => {
        if (!visible) {
            return;
        }

        setFile(undefined);
        setSource('upload');
        setCleansedFileName(undefined);
        setWorksheetIndex(0);
        setMappings({});
        setAdditionalHeaders([]);
        setIsFinalImport(false);
        setError(undefined);
        setSections({ upload: true, map: false, unmapped: false });
        preselectedFor.current = undefined;
    }, [visible, dataSourceTypeId]);

    useEffect(() => {
        const firstUsableWorksheet = previews.find(
            (worksheet) => !worksheet.error
        );
        setWorksheetIndex(firstUsableWorksheet?.index ?? 0);
    }, [worksheetPreviews]);

    const selectedWorksheetError = selectedWorksheet?.error;

    // Pre-selects only after a file is uploaded and its headers are known, once per upload and worksheet.
    useEffect(() => {
        if (
            !previousColumns?.length ||
            uploadId === undefined ||
            !selectedWorksheet ||
            selectedWorksheetError ||
            headers.length === 0
        ) {
            return;
        }

        const key = `${uploadId}:${selectedWorksheet.index}`;
        if (preselectedFor.current === key) {
            return;
        }
        preselectedFor.current = key;

        const preselected = preselectFromPreviousImport(
            previousColumns,
            mappingHeaders,
            requiredFields.map((field) => field.fieldName),
            isProjectData ? maxProjectAdditionalColumns : undefined
        );

        if (
            Object.keys(preselected.mappings).length === 0 &&
            preselected.additionalHeaders.length === 0
        ) {
            return;
        }

        setMappings(preselected.mappings);
        setAdditionalHeaders(preselected.additionalHeaders);
        setSections({
            upload: false,
            map: true,
            unmapped: preselected.additionalHeaders.length > 0,
        });
    }, [
        previousColumns,
        uploadId,
        selectedWorksheet,
        selectedWorksheetError,
        headers,
    ]);

    useEffect(() => {
        if (previewError) {
            showToast('Import', previewError, 'error');
        }
    }, [previewError]);

    useEffect(() => {
        if (selectedWorksheetError) {
            showToast('Import', selectedWorksheetError, 'error');
        }
    }, [selectedWorksheetError]);

    const resetMappings = (): void => {
        setMappings({});
        setAdditionalHeaders([]);
        setSections({ upload: false, map: true, unmapped: false });
    };

    const handleFileChange = (selectedFile?: File): void => {
        setError(undefined);

        if (!selectedFile) {
            return;
        }

        setFile(selectedFile);

        const extension = selectedFile.name.split('.').pop()?.toLowerCase();
        const isExcelExtension = extension === 'xls' || extension === 'xlsx';
        if (
            selectedFile.name.toLowerCase().includes('.exe.') ||
            !isExcelExtension ||
            (selectedFile.type !== '' &&
                !permittedExcelContentTypes.includes(selectedFile.type))
        ) {
            setFile(undefined);
            setError('Only Excel file types (.xls and .xlsx) are supported.');
            return;
        }

        if (selectedFile.size > maxFileSizeBytes) {
            setFile(undefined);
            setError('File size must not exceed 100 MB.');
            return;
        }

        setFile(selectedFile);
        resetMappings();
        onFileSelected?.(selectedFile);
    };

    const handleUploadChange = (uploadFile: FileModel): void => {
        const sourceFile = uploadFile.originFile;

        if (!(sourceFile instanceof File)) {
            setError('The selected file could not be read.');
            return;
        }

        handleFileChange(sourceFile);
    };

    const handleWorksheetChange = (selectedIndex: number): void => {
        setWorksheetIndex(selectedIndex);
        resetMappings();
    };

    const availableHeaders = (fieldName: string): string[] => {
        const selectedHeader = mappings[fieldName];
        return mappingHeaders.filter(
            (header) =>
                header === selectedHeader || !mappedHeaders.includes(header)
        );
    };

    const handleMappingChange = (
        fieldName: string,
        sourceHeader: string
    ): void => {
        setMappings((currentMappings) => ({
            ...currentMappings,
            [fieldName]: sourceHeader,
        }));
        setAdditionalHeaders((currentHeaders) =>
            currentHeaders.filter((header) => header !== sourceHeader)
        );
    };

    const toggleAdditionalHeader = (header: string, checked: boolean): void => {
        setAdditionalHeaders((currentHeaders) =>
            checked
                ? [...currentHeaders, header]
                : currentHeaders.filter(
                      (currentHeader) => currentHeader !== header
                  )
        );
    };

    const toggleSection = (section: 'upload' | 'map' | 'unmapped'): void => {
        setSections((currentSections) => ({
            ...currentSections,
            [section]: !currentSections[section],
        }));
    };

    const hasSource =
        source === 'upload' ? Boolean(file) : Boolean(cleansedFileName);

    const handleSourceChange = (value: 'upload' | 'cleansed'): void => {
        setSource(value);
        setFile(undefined);
        setCleansedFileName(undefined);
        setError(undefined);
        resetMappings();
        setSections({ upload: true, map: false, unmapped: false });
    };

    const handleCleansedFileReady = (response: ImportUploadResponse): void => {
        setCleansedFileName(response.fileName ?? 'Cleansed file');
        resetMappings();
        onCleansedFileSelected?.(response);
    };

    const handleSubmit = (): void => {
        if (
            !hasSource ||
            uploadId === undefined ||
            !selectedWorksheet ||
            selectedWorksheet.error ||
            !isMappingComplete ||
            hasTooManyAdditionalColumns
        ) {
            return;
        }

        onSubmit({
            uploadId,
            worksheetIndex: selectedWorksheet.index,
            worksheetName: selectedWorksheet.name,
            baseMappings: requiredFields.map((field) => ({
                ...field,
                sourceHeader: mappings[field.fieldName],
            })),
            additionalHeaders,
            mode,
            isFinalImport: isProjectData && isFinalImport,
        });
    };

    const previewMessage = isPreviewLoading
        ? 'Reading workbook columns...'
        : file && worksheetPreviews.length === 0 && !previewError
          ? 'Workbook columns will be available after the import service validates this file.'
          : undefined;

    return (
        <AppkitModal
            visible={visible}
            onCancel={onClose}
            modalStyle={{ width: '760px' }}
            footer={null}
        >
            <div className="import-data-modal">
                <div className="import-data-modal__intro">
                    <h2>Map columns to required fields</h2>
                    <p>
                        Select a workbook, then map its validated columns to the
                        required fields.
                    </p>
                </div>
                {error && (
                    <p className="import-data-modal__message import-data-modal__message--error">
                        {error}
                    </p>
                )}
                {previewMessage && (
                    <p className="import-data-modal__message import-data-modal__message--warning">
                        {previewMessage}
                    </p>
                )}

                <section className="import-data-modal__section">
                    <button
                        className="import-data-modal__toggle"
                        type="button"
                        onClick={() => toggleSection('upload')}
                        aria-expanded={sections.upload}
                    >
                        <span>Choose a data source</span>
                        <span
                            className={`Appkit4-icon ${
                                sections.upload
                                    ? 'icon-up-chevron-outline'
                                    : 'icon-down-chevron-outline'
                            }`}
                            aria-hidden="true"
                        />
                    </button>
                    {sections.upload && (
                        <div className="import-data-modal__content">
                            <RadioGroup
                                value={source}
                                onChange={(value: string) =>
                                    handleSourceChange(
                                        value as 'upload' | 'cleansed'
                                    )
                                }
                            >
                                <Radio value="upload">Upload a file</Radio>
                                <Radio value="cleansed">
                                    Use a cleansed file
                                </Radio>
                            </RadioGroup>
                            {source === 'cleansed' ? (
                                <>
                                    <CleanseFileSource
                                        entityId={entityId}
                                        timeId={timeId}
                                        dataType={
                                            dataSourceTypeId ===
                                            DataSourceTypeId.FixedAsset
                                                ? CleanseDataType.NUMBER_7
                                                : CleanseDataType.NUMBER_6
                                        }
                                        maxFileSizeBytes={maxFileSizeBytes}
                                        onImportSourceReady={
                                            handleCleansedFileReady
                                        }
                                        onError={setError}
                                    />
                                    {cleansedFileName && (
                                        <p className="import-data-modal__file-name">
                                            {cleansedFileName}
                                        </p>
                                    )}
                                </>
                            ) : (
                                <Upload
                                    onChange={handleUploadChange}
                                    multiple={false}
                                    autoUpload={false}
                                    acceptFileType=".XLS,.XLSX"
                                    uploadTitle="Upload your file"
                                    uploadInstruction="You can upload Excel files (.xls, .xlsx). The max file size is 100 MB."
                                    maxFileSize={maxFileSizeBytes}
                                    config={{
                                        trigger: false,
                                        type: 'inline',
                                        size: true,
                                    }}
                                />
                            )}
                            {source === 'upload' && file && (
                                <p className="import-data-modal__file-name">
                                    {file.name}
                                </p>
                            )}
                        </div>
                    )}
                </section>

                {previews.length > 1 && (
                    <div className="import-data-modal__worksheets">
                        <div className="import-data-modal__worksheets-intro">
                            <span
                                className="Appkit4-icon icon-document-text-outline"
                                aria-hidden="true"
                            />
                            <div>
                                <p className="import-data-modal__worksheets-title">
                                    This workbook has {previews.length}{' '}
                                    worksheets
                                </p>
                                <p className="import-data-modal__hint">
                                    Only the worksheet you choose here is
                                    imported.
                                </p>
                            </div>
                        </div>
                        <div className="import-data-modal__worksheet-options">
                            {previews.map((worksheet) => (
                                <button
                                    type="button"
                                    key={worksheet.index}
                                    className={`import-data-modal__worksheet${
                                        worksheet.index === worksheetIndex
                                            ? ' import-data-modal__worksheet--selected'
                                            : ''
                                    }${
                                        worksheet.error
                                            ? ' import-data-modal__worksheet--invalid'
                                            : ''
                                    }`}
                                    aria-pressed={
                                        worksheet.index === worksheetIndex
                                    }
                                    onClick={() =>
                                        handleWorksheetChange(worksheet.index)
                                    }
                                >
                                    <span className="import-data-modal__worksheet-name">
                                        {worksheet.name}
                                    </span>
                                    <span className="import-data-modal__worksheet-meta">
                                        {worksheet.error
                                            ? 'Cannot be imported'
                                            : `${worksheet.rowCount.toLocaleString()} rows · ${
                                                  worksheet.headers.length
                                              } columns`}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <section className="import-data-modal__section">
                    <button
                        className="import-data-modal__toggle"
                        type="button"
                        onClick={() => toggleSection('map')}
                        aria-expanded={sections.map}
                    >
                        <span>Map columns</span>
                        <span
                            className={`Appkit4-icon ${
                                sections.map
                                    ? 'icon-up-chevron-outline'
                                    : 'icon-down-chevron-outline'
                            }`}
                            aria-hidden="true"
                        />
                    </button>
                    {sections.map && (
                        <div className="import-data-modal__content">
                            {headers.length === 0 ? (
                                <p className="import-data-modal__hint">
                                    Workbook columns will appear here once the
                                    selected file has been validated.
                                </p>
                            ) : (
                                requiredFields.map((field) => (
                                    <label
                                        className="import-data-modal__mapping"
                                        key={field.fieldName}
                                    >
                                        {field.displayName}
                                        <select
                                            value={
                                                mappings[field.fieldName] ?? ''
                                            }
                                            onChange={(event) =>
                                                handleMappingChange(
                                                    field.fieldName,
                                                    event.target.value
                                                )
                                            }
                                        >
                                            <option value="">
                                                Choose column
                                            </option>
                                            {availableHeaders(
                                                field.fieldName
                                            ).map((header) => (
                                                <option
                                                    key={header}
                                                    value={header}
                                                >
                                                    {header}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ))
                            )}
                        </div>
                    )}
                </section>

                <section className="import-data-modal__section">
                    <button
                        className="import-data-modal__toggle"
                        type="button"
                        onClick={() => toggleSection('unmapped')}
                        aria-expanded={sections.unmapped}
                    >
                        <span>Unmapped columns</span>
                        <span
                            className={`Appkit4-icon ${
                                sections.unmapped
                                    ? 'icon-up-chevron-outline'
                                    : 'icon-down-chevron-outline'
                            }`}
                            aria-hidden="true"
                        />
                    </button>
                    {sections.unmapped && (
                        <div className="import-data-modal__content">
                            {headers.length === 0 ? (
                                <p className="import-data-modal__hint">
                                    Additional columns can be selected after
                                    workbook validation.
                                </p>
                            ) : (
                                <>
                                    <p className="import-data-modal__hint">
                                        Select columns to add as additional
                                        fields.
                                    </p>
                                    <Checkbox
                                        checked={
                                            unmappedHeaders.length > 0 &&
                                            additionalHeaders.length ===
                                                unmappedHeaders.length
                                        }
                                        onChange={(checked: boolean) =>
                                            setAdditionalHeaders(
                                                checked ? unmappedHeaders : []
                                            )
                                        }
                                    >
                                        Select all
                                    </Checkbox>
                                    <div className="import-data-modal__checkboxes">
                                        {unmappedHeaders.map((header) => (
                                            <Checkbox
                                                key={header}
                                                checked={additionalHeaders.includes(
                                                    header
                                                )}
                                                onChange={(checked: boolean) =>
                                                    toggleAdditionalHeader(
                                                        header,
                                                        checked
                                                    )
                                                }
                                            >
                                                {header}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </section>

                <footer className="import-data-modal__footer">
                    {isProjectData && (
                        <div className="import-data-modal__mode">
                            <Checkbox
                                checked={isFinalImport}
                                onChange={setIsFinalImport}
                            >
                                Final Value?
                            </Checkbox>
                        </div>
                    )}
                    {!isProjectData && hasExistingData && (
                        <div className="import-data-modal__mode">
                            <RadioGroup
                                value={mode}
                                onChange={(value: number) =>
                                    setMode(value as ImportMode)
                                }
                            >
                                <Radio value={ImportMode.Append}>
                                    Add to the existing data
                                </Radio>
                                <Radio value={ImportMode.Replace}>
                                    Replace the existing data
                                </Radio>
                            </RadioGroup>
                            {mode === ImportMode.Replace && (
                                <p className="import-data-modal__warning">
                                    This permanently removes every existing row
                                    for this period, along with its pool
                                    assignments and reviews. Rules are kept and
                                    re-pointed at the new columns where
                                    possible.
                                </p>
                            )}
                        </div>
                    )}
                    <Button kind="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        kind="primary"
                        disabled={
                            !hasSource ||
                            uploadId === undefined ||
                            !selectedWorksheet ||
                            Boolean(selectedWorksheet.error) ||
                            !isMappingComplete ||
                            hasTooManyAdditionalColumns ||
                            isPreviewLoading ||
                            isSubmitting
                        }
                        onClick={handleSubmit}
                    >
                        {isSubmitting
                            ? 'Importing…'
                            : mode === ImportMode.Replace
                              ? 'Replace data'
                              : 'Import'}
                    </Button>
                </footer>
            </div>
        </AppkitModal>
    );
};

export default ImportDataModal;
